import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import useSWR from 'swr';
import { Bookmark } from '../../../../../../../typings/Bookmark.interface';
import { APIWebhook } from '../../../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { useCopyToClipboard } from '../../../../utils/copy';
import Button, { ClickableArea } from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Chip from '../../../common/base/Chip';
import Loading from '../../../common/base/Loading';
import Tabs from '../../../common/base/Tabs';
import Text from '../../../common/base/Text';
import { useDialog } from '../../../common/Dialog';
import SelectResourceInput from '../../../common/Form/Fields/SelectResourceInput';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import ConfirmDeleteDialog from '../Connections/ConfirmDeleteDialog';
import { StyledViewNavHeader, StyledViewNavSecondary, StyledViewSidebarScrollableSection, StyledViewSidebar, StyledViewSidebarNav, StyledViewSidebarSection, ViewSidebarTitle, StyledViewSidebarActions } from '../../../layouts/ViewLayout';
import { MetadataGrid } from '../../../common/MetadataGrid';
import Badge from '../../../common/base/Badge';
import { HeadersCollapsibleSection } from '../../../common/Data/Headers';
import { QueryCollapsibleSection } from '../../../common/Data/Query';
import { BodyCollapsibleSection } from '../../../common/Data/Body';
const BookmarkDetails: React.FC<{
  bookmark_id: string;
  onUpdate: (bookmark: Bookmark) => void;
  onReplay: (bookmark: Bookmark) => void;
  onDelete: (id: String) => void;
  onClose: () => void;
}> = t0 => {
  const $ = _c(68);
  const {
    bookmark_id,
    onClose,
    onUpdate,
    onDelete,
    onReplay
  } = t0;
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const copyToClipboard = useCopyToClipboard();
  const [active_tab, setActiveTab] = useLocalStorage("pref:bookmark-sidebar:active-tab", "request");
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] !== bookmark_id) {
    t1 = APIMethodKeys.bookmarks.get(bookmark_id);
    $[0] = bookmark_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== HookdeckAPI.bookmarks || $[3] !== bookmark_id) {
    t2 = () => HookdeckAPI.bookmarks.get(bookmark_id);
    $[2] = HookdeckAPI.bookmarks;
    $[3] = bookmark_id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: bookmark,
    mutate
  } = useSWR(t1, t2);
  let t3;
  if ($[5] !== bookmark) {
    t3 = bookmark && APIMethodKeys.webhooks.get(bookmark.webhook_id);
    $[5] = bookmark;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== HookdeckAPI.webhooks || $[8] !== bookmark?.webhook_id) {
    t4 = () => HookdeckAPI.webhooks.get(bookmark?.webhook_id as string);
    $[7] = HookdeckAPI.webhooks;
    $[8] = bookmark?.webhook_id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: webhook
  } = useSWR(t3, t4);
  let t5;
  if ($[10] !== HookdeckAPI.bookmarks || $[11] !== HookdeckAPI.webhooks.list || $[12] !== addToast || $[13] !== bookmark?.label || $[14] !== bookmark_id || $[15] !== mutate || $[16] !== onUpdate || $[17] !== showDialog || $[18] !== webhook?.id) {
    t5 = () => {
      showDialog(values => HookdeckAPI.bookmarks.update(bookmark_id, {
        label: values.label,
        webhook_id: values.webhook
      }).then(updated_bookmark => {
        mutate(updated_bookmark);
        onUpdate(updated_bookmark);
        addToast("success", "Bookmark updated");
      }).catch(() => {
        addToast("error", "Bookmark failed to update");
      }), _temp, {
        title: "Edit bookmark",
        submit_label: "Save",
        cancel_label: "Cancel",
        form_props: {
          initial_values: {
            label: bookmark?.label ?? "",
            webhook: webhook?.id
          },
          validate: _temp2,
          Fields: () => <><TextInput label="Bookmark Label" name="label" required={true} /><SelectResourceInput name="webhook" label="Connection" getLabel={_temp3} search_key="full_name" listResources={HookdeckAPI.webhooks.list} listResourcesKey={APIMethodKeys.webhooks.list} /></>
        }
      });
    };
    $[10] = HookdeckAPI.bookmarks;
    $[11] = HookdeckAPI.webhooks.list;
    $[12] = addToast;
    $[13] = bookmark?.label;
    $[14] = bookmark_id;
    $[15] = mutate;
    $[16] = onUpdate;
    $[17] = showDialog;
    $[18] = webhook?.id;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const showEdit = t5;
  let t6;
  if ($[20] !== HookdeckAPI.bookmarks || $[21] !== addToast || $[22] !== bookmark || $[23] !== onClose || $[24] !== onDelete || $[25] !== showDialog) {
    t6 = () => {
      if (!bookmark) {
        return;
      }
      showDialog(() => {
        HookdeckAPI.bookmarks.delete(bookmark.id).then(t7 => {
          const {
            id
          } = t7;
          onClose();
          onDelete(id);
          addToast("success", "Bookmark deleted");
        }).catch(() => addToast("error", "Failed to remove bookmark"));
      }, _temp4, {
        title: "Delete bookmark",
        submit_label: "Delete bookmark",
        submit_icon: "delete",
        cancel_label: "Keep bookmark",
        danger: true,
        message: <ConfirmDeleteDialog text="By deleting this bookmark, the associated request data will no longer be exempted from your archival window." />
      });
    };
    $[20] = HookdeckAPI.bookmarks;
    $[21] = addToast;
    $[22] = bookmark;
    $[23] = onClose;
    $[24] = onDelete;
    $[25] = showDialog;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const handleDelete = t6;
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ViewSidebarTitle title="Bookmark" />;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== bookmark || $[29] !== onReplay) {
    t8 = <Button.Permission role="member" ghost={true} icon="replay" title="Replay" onClick={() => bookmark && onReplay(bookmark)} />;
    $[28] = bookmark;
    $[29] = onReplay;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== showEdit) {
    t9 = <Button.Permission role="member" ghost={true} icon="edit" onClick={() => showEdit()} />;
    $[31] = showEdit;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== handleDelete) {
    t10 = <Button.Permission role="member" ghost={true} icon="delete" onClick={handleDelete} />;
    $[33] = handleDelete;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Divider vertical={true} m={{
      y: 1,
      x: 2
    }} />;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== onClose) {
    t12 = <Button ghost={true} icon="close" onClick={onClose} />;
    $[36] = onClose;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t12 || $[40] !== t8 || $[41] !== t9) {
    t13 = <StyledViewNavHeader>{t7}<StyledViewSidebarActions>{t8}{t9}{t10}{t11}{t12}</StyledViewSidebarActions></StyledViewNavHeader>;
    $[38] = t10;
    $[39] = t12;
    $[40] = t8;
    $[41] = t9;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== setActiveTab) {
    t14 = tab => setActiveTab(tab as 'request' | 'details');
    $[43] = setActiveTab;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [{
      key: "request",
      label: "Request"
    }, {
      key: "details",
      label: "Details"
    }];
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== active_tab || $[47] !== t14) {
    t16 = <StyledViewNavSecondary><Tabs active_tab={active_tab} border={false} onTabSelected={t14} tabs={t15} /></StyledViewNavSecondary>;
    $[46] = active_tab;
    $[47] = t14;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t13 || $[50] !== t16) {
    t17 = <StyledViewSidebarNav>{t13}{t16}</StyledViewSidebarNav>;
    $[49] = t13;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== active_tab || $[53] !== bookmark || $[54] !== copyToClipboard || $[55] !== webhook) {
    t18 = active_tab === "request" && <Loading require={[bookmark, bookmark?.data, webhook]} wrapper={_temp5}>{() => <><Div flex={{
          align: "center",
          gap: 2,
          justify: "flex-start"
        }} p={{
          x: 6,
          y: 3
        }}><Div flex={{
            align: "center",
            gap: 2
          }} style={{
            overflow: "hidden"
          }}><Badge neutral={true}>{bookmark.data.headers["x-hookdeck-original-method"] || "POST"}</Badge><Text mono={true} as="span" title={bookmark.data.path} ellipsis={true} size="s" w={100}>{bookmark.data.path || "/"}</Text></Div><Button ghost={true} onClick={() => copyToClipboard(bookmark.data.path || "/")} icon="copy" /></Div><Divider /><HeadersCollapsibleSection headers={bookmark?.data?.headers || {}} embedded={true} />{bookmark.data.query && <QueryCollapsibleSection query={bookmark.data.query} parsed_query={bookmark.data.parsed_query || {}} embedded={true} />}<BodyCollapsibleSection body={bookmark.data.body} body_format={bookmark.data.body_format} headers={bookmark.data.headers} embedded={true} type="bookmark" id={bookmark?.id} /></>}</Loading>;
    $[52] = active_tab;
    $[53] = bookmark;
    $[54] = copyToClipboard;
    $[55] = webhook;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== active_tab || $[58] !== bookmark || $[59] !== copyToClipboard || $[60] !== webhook) {
    t19 = active_tab === "details" && <Loading require={[bookmark, webhook]} wrapper={_temp6}>{() => <Div p={{
        x: 6
      }}><MetadataGrid embedded={true}><Text size="s" muted={true}>Bookmark Label</Text><Text>{bookmark?.label}</Text></MetadataGrid><Divider /><MetadataGrid embedded={true}><Text size="s" muted={true}>Connection</Text><Chip outline={true} mono={true} label={webhook.full_name} to={`/connections/${bookmark.webhook_id}`} /></MetadataGrid><Divider /><MetadataGrid embedded={true}><Text size="s" muted={true}>Event Data ID</Text><ClickableArea rounded={true} block={false} flex={{
            align: "center"
          }} onClick={() => copyToClipboard(bookmark?.event_data_id || "")}><Text muted={true} mono={true}>{bookmark?.event_data_id}</Text><Icon right={true} icon="copy" muted={true} /></ClickableArea></MetadataGrid></Div>}</Loading>;
    $[57] = active_tab;
    $[58] = bookmark;
    $[59] = copyToClipboard;
    $[60] = webhook;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== t18 || $[63] !== t19) {
    t20 = <StyledViewSidebarScrollableSection>{t18}{t19}</StyledViewSidebarScrollableSection>;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== t17 || $[66] !== t20) {
    t21 = <StyledViewSidebar>{t17}{t20}</StyledViewSidebar>;
    $[65] = t17;
    $[66] = t20;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  return t21;
};
export default BookmarkDetails;
function _temp() {
  return null;
}
function _temp2(t0) {
  const {
    label
  } = t0;
  if (!label || label.length < 0) {
    return {
      label: "Required"
    };
  }
  return {};
}
function _temp3(r) {
  return r.full_name;
}
function _temp4() {
  return null;
}
function _temp5(loading) {
  return <StyledViewSidebarSection>{loading}</StyledViewSidebarSection>;
}
function _temp6(loading_0) {
  return <StyledViewSidebarSection>{loading_0}</StyledViewSidebarSection>;
}